/**
 * BlockActionsMenu — TASK-013 per-block micro-menu (Phase A "Copia" + Phase B
 * "Chat"), mounted into the shared `@arianna/reader-ui` `ReaderDocument` via
 * its optional `renderBlockActions` render-prop (see ReaderDocument.tsx).
 * apps/desktop does not pass that prop, so this component never renders
 * there — it is intentionally web-only, per TASK-013's Architecture
 * Decision #2 ("Menu hard-coded dentro BlockRenderer — scartato").
 *
 * Position — LEFT GUTTER, never over the text (fixed after visual QA showed
 * a top-right overlay covering body copy):
 *   `right-full` anchors the menu's RIGHT edge at the block wrapper's own
 *   left edge — i.e. the menu can only ever render at x<0, strictly outside
 *   the block's own box, never over the text. Top-aligned with the block
 *   (`top-0`) rather than vertically centered — reads as attached to the
 *   start of the block regardless of how long it runs.
 *
 * Per-kind horizontal compensation (visual QA caught plain blocks sitting
 * ~4px further left than integration/correction/clarification ones):
 *   CSS resolves absolute-positioning offsets like `right-full` against the
 *   positioned ancestor's PADDING box, not its border box. A `paragraph`
 *   block's wrapper has no border, so its padding box and border box share
 *   the same left edge. integration/correction/clarification wrappers carry
 *   `border-l-4` (see ReaderDocument.tsx `BlockRenderer`) — a 4px LEFT
 *   border only — which shifts their padding box 4px to the right of their
 *   (visible, colored) border box. Left uncompensated, `right-full` lands
 *   the menu 4px closer to those blocks than to a plain paragraph. `GAP`
 *   below adds that 4px back for bordered kinds so every kind's menu sits
 *   the same distance from the block's true visual left edge (its outer
 *   border-box edge — the colored line itself for bordered kinds).
 *
 * Two size tiers (mobile-first: unprefixed = base, `lg:` = ≥1024px) —
 * `ReaderDocument`'s `<article>` is `mx-auto max-w-[760px] px-8`: that `px-8`
 * (32px) is a GUARANTEED left margin around every block at every viewport
 * width, independent of how much (if any) extra centering margin exists
 * outside the article. The base tier's whole footprint (button + gap) is
 * sized to fit inside that guaranteed 32px, so it can NEVER cause the page
 * to overflow/clip, no matter how narrow the viewport gets. Once the
 * viewport is wide enough that the article is at its capped 760px width
 * with real room to spare outside it (verified ≥1024px gives ~160px of
 * safety margin over the ~864px actually needed), `lg:` switches to the
 * roomier tier that honours the ~16px safe-area Dio asked for. Test at a
 * narrow viewport with the TOC sidebar CLOSED — the sidebar is a fixed
 * 280px flex sibling with no responsive collapse of its own (pre-existing,
 * out of scope here), so a narrow viewport WITH the sidebar open leaves too
 * little room for the reading column itself, independent of this menu.
 *
 * Reveal behaviour (TASK-013 Architecture Decision #5, "no hover-only"):
 *   - Fine pointer (mouse): invisible at rest, revealed on hover of the
 *     WHOLE block (the reader-ui wrapper carries `group` when this prop is
 *     passed) or on keyboard focus of a button inside the menu.
 *   - Coarse pointer (touch): always fully visible — a touch device has no
 *     hover concept, so a "reveal on hover" affordance would be permanently
 *     undiscoverable. This is the "reso raggiungibile via tap" resolution:
 *     the icon buttons themselves ARE the reachable target, no separate
 *     reveal step. Tailwind v4's `pointer-coarse:` variant maps directly to
 *     the CSS `(pointer: coarse)` media feature — no custom variant needed.
 *     Real touch devices are narrow, so they get the base (safe) size tier.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { BundleManualeBlock } from '@arianna/shared'
import { copyBlockToClipboard } from './blockActions.js'

export interface BlockActionsMenuProps {
  block: BundleManualeBlock
  subParaId: string
  /** Phase B — omit to render Copia only (e.g. future non-tutor surfaces). */
  onChat?: (args: { blockId: string; subParaId: string }) => void
}

const WRAPPER_BASE_CLASS =
  'absolute right-full top-0 z-10 flex flex-col items-center gap-1 ' +
  'lg:rounded-md lg:border lg:border-line lg:bg-surface/95 lg:px-1 lg:py-1 lg:shadow-sm ' +
  'opacity-0 transition-opacity duration-150 ' +
  'group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100'

/** `border-l-4` = 4px. Bordered kinds get +4px of right-margin to cancel
 *  the padding-box inset described in the module doc above. */
const GAP = {
  plain: 'mr-1 lg:mr-4',
  bordered: 'mr-2 lg:mr-5',
}

const BUTTON_CLASS =
  'inline-flex items-center justify-center size-5 lg:size-7 rounded text-ink-3 bg-transparent ' +
  'border-none cursor-pointer hover:text-ink hover:bg-surface-2 transition-colors'

const COPIED_FEEDBACK_MS = 1600

export function BlockActionsMenu({ block, subParaId, onChat }: BlockActionsMenuProps) {
  const [copied, setCopied] = useState(false)
  const copiedTimeoutRef = useRef<number | null>(null)

  // Every kind except `paragraph` carries `border-l-4` on its wrapper (see
  // module doc — GAP compensates for the resulting padding-box inset).
  const wrapperClassName = `${WRAPPER_BASE_CLASS} ${block.kind === 'paragraph' ? GAP.plain : GAP.bordered}`

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current != null) window.clearTimeout(copiedTimeoutRef.current)
    },
    [],
  )

  const handleCopy = useCallback(() => {
    void copyBlockToClipboard(block.markdown)
      .then(() => {
        setCopied(true)
        if (copiedTimeoutRef.current != null) window.clearTimeout(copiedTimeoutRef.current)
        copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
      })
      .catch(() => {
        // Clipboard denied/unavailable (non-secure context, permission
        // refusal) — client-side only action, nothing to retry or report
        // beyond simply not showing the "Copiato" confirmation.
      })
  }, [block.markdown])

  const handleChat = useCallback(() => {
    onChat?.({ blockId: block.id, subParaId })
  }, [onChat, block.id, subParaId])

  return (
    <div className={wrapperClassName} data-testid="block-actions-menu">
      <button
        type="button"
        onClick={handleCopy}
        title="Copia"
        aria-label="Copia il testo del blocco"
        className={BUTTON_CLASS}
      >
        {copied ? (
          <svg
            width="15"
            height="15"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="4 10.5 8 14.5 16 5.5" />
          </svg>
        ) : (
          <svg
            width="15"
            height="15"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="7" y="7" width="10" height="10" rx="1.5" />
            <path d="M4 13V4.5A1.5 1.5 0 0 1 5.5 3H13" />
          </svg>
        )}
      </button>

      {onChat != null && (
        <button
          type="button"
          onClick={handleChat}
          title="Chat sul blocco"
          aria-label="Apri il tutor su questo blocco"
          className={BUTTON_CLASS}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 4.5A1.5 1.5 0 0 1 4.5 3h11A1.5 1.5 0 0 1 17 4.5v7A1.5 1.5 0 0 1 15.5 13H8l-4 3v-3H4.5A1.5 1.5 0 0 1 3 11.5v-7Z" />
          </svg>
        </button>
      )}

      <span role="status" aria-live="polite" className="sr-only">
        {copied ? 'Copiato' : ''}
      </span>
    </div>
  )
}
